import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Button, Select } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { usePlanAccess } from '../api/PlanAccess';
import { useStoreSelection } from '../api/StoreSelection';

interface AdminHeaderProps {
  children: React.ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  match?: (pathname: string) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Dashboard', match: (p) => p === '/admin' },
  { to: '/admin/links', label: 'Links' },
];

const HEADER_TEXT = 'text-base font-medium';
const NAV_LINK_BASE =
  `inline-flex items-center px-1 pt-1 border-b-2 ${HEADER_TEXT} transition-colors`;
const NAV_LINK_INACTIVE =
  'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300';
const NAV_LINK_ACTIVE = 'border-blue-500 text-gray-900';
const ACTION_LINK =
  `inline-flex items-center gap-1.5 ${HEADER_TEXT} text-gray-500 hover:text-gray-700 transition-colors`;

function isVerticalViewport(): boolean {
  return window.innerHeight > window.innerWidth;
}

function navLinkClass(active: boolean): string {
  return `${NAV_LINK_BASE} ${active ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE}`;
}

function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.match) {
    return item.match(pathname);
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessStores } = usePlanAccess();
  const {
    loading: storesLoading,
    stores,
    selectedStoreId,
    setSelectedStoreId,
  } = useStoreSelection();
  const isAdminPage = location.pathname.includes('/admin');
  const [isVertical, setIsVertical] = useState(isVerticalViewport);

  useEffect(() => {
    const handleResize = () => setIsVertical(isVerticalViewport());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const storeOptions = useMemo(
    () =>
      stores.map((store) => ({
        value: store.id,
        label: store.name,
      })),
    [stores]
  );

  const settingsItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'Profile Setting',
      onClick: () => navigate('/admin/profile'),
    },
    ...(canAccessStores
      ? [
          {
            key: 'stores',
            label: 'Manage Stores',
            onClick: () => navigate('/admin/stores'),
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {isAdminPage && (
        <nav className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className={`flex justify-between h-16 ${isVertical ? 'flex-col h-auto' : ''}`}
            >
              <div
                className={`flex ${isVertical ? 'flex-col items-start' : ''}`}
              >
                <div className="flex-shrink-0 flex items-center">
                  <Link to="/admin" className="text-xl font-bold text-gray-800">
                    ViooVIP Admin
                  </Link>
                </div>
                <div
                  className={`ml-6 flex space-x-8 ${isVertical ? 'flex-col space-x-0 space-y-2 mt-2' : ''}`}
                >
                  {NAV_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={navLinkClass(
                        isNavItemActive(item, location.pathname)
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              <div
                className={`flex items-center gap-4 ${isVertical ? 'mt-2 self-start' : ''}`}
              >
                {canAccessStores && (
                  <div className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1">
                    <span className={`${HEADER_TEXT} text-gray-500 whitespace-nowrap`}>
                      Current store
                    </span>
                    <Select
                      value={selectedStoreId ?? undefined}
                      options={storeOptions}
                      onChange={(value: number) => setSelectedStoreId(value)}
                      loading={storesLoading}
                      placeholder="Select store"
                      variant="borderless"
                      style={{ minWidth: 140 }}
                      aria-label="Current store"
                      popupMatchSelectWidth={false}
                    />
                  </div>
                )}
                <Dropdown
                  menu={{ items: settingsItems }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    className={ACTION_LINK}
                    aria-label="Settings"
                    icon={<SettingOutlined className="text-gray-500" />}
                    size="large"
                    type="text"
                  />
                </Dropdown>
                <Link to="/logout" className={ACTION_LINK}>
                  Logout
                </Link>
              </div>
            </div>
          </div>
        </nav>
      )}
      <main>{children}</main>
    </div>
  );
};

export default AdminHeader;
